import "./App.css";
import { BrowserRouter, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Routers from "./routers/Router";
import { ToastContainer, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "./components/cookies/cookies";
import CookiePreferencesModal from "./components/cookies/CookiePreferencesModal";
import CookieFloatingButton from "./components/cookies/CookieFloatingButton";
import { HelmetProvider } from "react-helmet-async";
import SEO from "./components/seo/SEO";
import { trackPageView } from "./utils/analytics";
import {
  CONSENT_SYNC_KEY,
  bootstrapLanguageRouting,
  clearAccessLangCookie,
  getAccessLangCookie,
  getCookiePreferences,
  isFunctionalAllowed,
  normalizeLang,
  savePreferencesCookie,
  setAccessLangCookie,
} from "./utils/cookieConsent";

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView();
  }, [location]);

  return null;
}

function getBasename() {
  const pathname = window.location.pathname;

  if (pathname.startsWith("/vi")) {
    return "/vi";
  }

  if (pathname.startsWith("/en")) {
    return "/en";
  }

  return "";
}

const pathname = window.location.pathname;

if (
  pathname === "/uploads" ||
  pathname.startsWith("/uploads/")
) {
  const savedLang =
    (isFunctionalAllowed() && getAccessLangCookie()) ||
    normalizeLang(localStorage.getItem("lang")) ||
    "en";
  const lang = savedLang === "vi" ? "vi" : "en";
  window.location.replace(pathname.replace("/uploads", `/${lang}/uploads`));
}

if (
  pathname === "/downloads" ||
  pathname.startsWith("/downloads/")
) {
  const savedLang =
    (isFunctionalAllowed() && getAccessLangCookie()) ||
    normalizeLang(localStorage.getItem("lang")) ||
    "en";
  const lang = savedLang === "vi" ? "vi" : "en";
  window.location.replace(pathname.replace("/downloads", `/${lang}/downloads`));
}

const isValidLangRoute =
  pathname === "/" ||
  pathname.startsWith("/en") ||
  pathname.startsWith("/vi");

if (!isValidLangRoute) {
  window.location.replace("/");
}

// Định tuyến ngôn ngữ theo consent + cookie (client-side)
bootstrapLanguageRouting();

const basename = getBasename();

const EMPTY_PREFS = {
  functional: false,
  analytics: false,
  marketing: false,
};

function App() {
  const { i18n } = useTranslation();
  const [, setNonce] = useState("");
  const [showCookies, setShowCookies] = useState(false);
  const [openPreferences, setOpenPreferences] = useState(false);
  const [showCookieButton, setShowCookieButton] = useState(false);
  // Trạng thái đã commit (đã Save) — Cancel không đụng tới
  const [preferences, setPreferences] = useState(EMPTY_PREFS);
  const savingRef = useRef(false);

  const applyPreferencesState = useCallback((prefs) => {
    const next = {
      functional: !!prefs?.functional,
      analytics: !!prefs?.analytics,
      marketing: !!prefs?.marketing,
    };
    setPreferences(next);
    return next;
  }, []);

  const saveCookies = useCallback(
    (prefs) => {
      if (savingRef.current) return applyPreferencesState(prefs);
      savingRef.current = true;
      try {
        const saved = savePreferencesCookie(prefs);
        applyPreferencesState(saved);
        return saved;
      } finally {
        savingRef.current = false;
      }
    },
    [applyPreferencesState]
  );

  // Đồng bộ i18n theo URL; chỉ ghi cookie/localStorage khi functional === true
  useEffect(() => {
    const lang = basename.replace("/", "") || "en";

    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }

    if (isFunctionalAllowed()) {
      setAccessLangCookie(lang);
    } else {
      clearAccessLangCookie();
    }
  }, [i18n]);

  // Load consent đã lưu
  useEffect(() => {
    const parsed = getCookiePreferences();
    if (!parsed) {
      setShowCookies(true);
      clearAccessLangCookie();
      return;
    }

    applyPreferencesState(parsed);
    setShowCookieButton(true);

    if (!parsed.functional) {
      clearAccessLangCookie();
    }
  }, [applyPreferencesState]);

  // Cross-tab + focus: luôn đọc lại cookie thật, không tin state cũ
  useEffect(() => {
    const refreshFromCookie = () => {
      const parsed = getCookiePreferences();
      if (!parsed) {
        applyPreferencesState(EMPTY_PREFS);
        clearAccessLangCookie();
        return;
      }

      applyPreferencesState(parsed);

      if (parsed.functional === true) {
        setAccessLangCookie(basename.replace("/", "") || "en");
      } else {
        clearAccessLangCookie();
      }
    };

    const onStorage = (event) => {
      if (event.key !== CONSENT_SYNC_KEY) return;
      refreshFromCookie();
    };

    // Chỉ sync từ tab khác / focus — tránh loop với event cùng tab (đã xử lý trong saveCookies)
    const onFocus = () => refreshFromCookie();
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshFromCookie();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [applyPreferencesState]);

  // Back/Forward: cập nhật cookie theo URL nếu functional đang bật
  useEffect(() => {
    const syncFromHistory = () => {
      if (!isFunctionalAllowed()) {
        clearAccessLangCookie();
        return;
      }
      const lang =
        window.location.pathname.startsWith("/vi") ? "vi" : "en";
      setAccessLangCookie(lang);
    };

    window.addEventListener("popstate", syncFromHistory);
    window.addEventListener("pageshow", syncFromHistory);
    return () => {
      window.removeEventListener("popstate", syncFromHistory);
      window.removeEventListener("pageshow", syncFromHistory);
    };
  }, []);

  useEffect(() => {
    axios
      .get("/auth/nonce")
      .then((res) => {
        const nonce = res.headers["x-nonce"];
        if (!nonce) return;

        setNonce(nonce);
        window.nonce = nonce;
        document
          .getElementById("nonce-meta")
          ?.setAttribute("data-nonce", nonce);

        document.querySelectorAll("script, style").forEach((tag) => {
          if (!tag.hasAttribute("nonce")) {
            tag.setAttribute("nonce", nonce);
          }
        });

        const script = document.createElement("script");
        script.src =
          "https://www.googletagmanager.com/gtag/js?id=G-F8435E3E2P";
        script.async = true;
        script.nonce = nonce;
        script.onload = () => {
          window.dataLayer = window.dataLayer || [];
          function gtag() {
            window.dataLayer.push(arguments);
          }
          window.gtag = gtag;
          window.gtag("js", new Date());
          window.gtag("config", "G-F8435E3E2P");
        };
        document.head.appendChild(script);
      })
      .catch(console.error);
  }, []);

  const handleAcceptAll = () => {
    const all = { functional: true, analytics: true, marketing: true };
    saveCookies(all);
    setShowCookies(false);
    setShowCookieButton(true);
  };

  const handleRejectAll = () => {
    const reject = { functional: false, analytics: false, marketing: false };
    saveCookies(reject);
    setShowCookies(false);
    setShowCookieButton(true);
  };

  const handleSavePreferences = (nextPrefs) => {
    saveCookies(nextPrefs ?? preferences);
    setOpenPreferences(false);
    setShowCookies(false);
    setShowCookieButton(true);
  };

  if (window.location.pathname === "/") return null;

  return (
    <HelmetProvider>
      <div className="App font-montserrat">
        <SEO />
        <BrowserRouter basename={basename}>
          <AnalyticsTracker />
          <Routers />

          {showCookies && (
            <Cookies
              onAcceptAll={handleAcceptAll}
              onRejectAll={handleRejectAll}
              onPreferences={() => setOpenPreferences(true)}
            />
          )}

          <CookiePreferencesModal
            open={openPreferences}
            onClose={() => setOpenPreferences(false)}
            preferences={preferences}
            onSave={handleSavePreferences}
          />

          {showCookieButton && (
            <CookieFloatingButton onClick={() => setOpenPreferences(true)} />
          )}

          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick={false}
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            transition={Bounce}
          />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
